import clsx from 'clsx';
import { useEffect, useState } from 'react';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useSurveyRequiredAnswers } from '@/hooks/useSurveyRequiredAnswers';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { setSurveyScreeningAnswers } from '@/stores/appSlice';
import { selectSurveyScreening } from '@/stores/selectors';
import { Navigate, useNavigate } from 'react-router-dom';

const AGE_BAND_LABELS = [
  '< 19',
  '19 - 24',
  '25 - 34',
  '35 - 44',
  '45 - 55',
  '55 >',
] as const;

type AgeBand = (typeof AGE_BAND_LABELS)[number];

const SMOKER_OPTIONS = [
  { id: 'yes' as const, label: 'Yes' },
  { id: 'no' as const, label: 'No' },
];

type SmokerChoice = (typeof SMOKER_OPTIONS)[number]['id'];

function ageBandFromStoredScreening(screening: { ageBand: string } | null): AgeBand | null {
  if (!screening) return null;
  return AGE_BAND_LABELS.includes(screening.ageBand as AgeBand) ? (screening.ageBand as AgeBand) : null;
}

const choiceButtonClass = (isSelected: boolean) =>
  clsx(
    'min-w-0 rounded-sm border px-2 py-3.5 text-center text-[18px] font-medium leading-tight text-white transition duration-150 sm:px-3 sm:text-[20px]',
    isSelected
      ? 'border-white/90 bg-white/30 font-semibold shadow-[0_0_0_2px_rgba(255,255,255,0.45),inset_0_1px_0_rgba(255,255,255,0.35)]'
      : 'border-white/35 bg-white/10 shadow-sm hover:border-white/55 hover:bg-white/18 active:scale-[0.99] active:bg-white/22'
  );

export default function AgeSmoker() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const screening = useAppSelector(selectSurveyScreening);
  const { warning, canProceed, dismissWarning } = useSurveyRequiredAnswers();
  const [selectedBand, setSelectedBand] = useState<AgeBand | null>(() =>
    ageBandFromStoredScreening(screening)
  );
  const [smokerChoice, setSmokerChoice] = useState<SmokerChoice | null>(() => screening?.smoker ?? null);

  useEffect(() => {
    if (screening == null) return;
    setSelectedBand(ageBandFromStoredScreening(screening));
    setSmokerChoice(screening.smoker);
  }, [screening]);

  useEffect(() => {
    dismissWarning();
  }, [selectedBand, smokerChoice, dismissWarning]);

  const handleSubmit = () => {
    const allAnswered = selectedBand != null && smokerChoice != null;
    if (!canProceed(allAnswered, t.ageSmoker.allRequiredWarning)) return;

    // Early exit: no screening data saved — thank-you only
    if (selectedBand === '< 19' || smokerChoice === 'no') {
      navigate('/thankyou');
      return;
    }

    dispatch(
      setSurveyScreeningAnswers({
        ageBand: selectedBand,
        smoker: smokerChoice,
      })
    );
    navigate('/pdpa');
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        <h1 className="pt-[15%] text-center text-[36px] sm:text-[42px] font-semibold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
          {t.ageSmoker.title}
        </h1>

        <SurveyBrownPanelInset className="mt-6">
          <SurveyBrownPanel>
            <div className="grid grid-cols-2 gap-2">
              {AGE_BAND_LABELS.map((label) => {
                const isSelected = selectedBand === label;
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelectedBand(label)}
                    className={choiceButtonClass(isSelected)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </SurveyBrownPanel>
        </SurveyBrownPanelInset>

        <h2 className="mt-8 px-4 text-center text-[36px] sm:text-[42px] font-semibold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
          {t.ageSmoker.smokerQuestion}
        </h2>

        <SurveyBrownPanelInset className="mt-6">
          <SurveyBrownPanel>
            <div className="grid grid-cols-2 gap-2">
              {SMOKER_OPTIONS.map(({ id }) => {
                const label = id === 'yes' ? t.ageSmoker.yes : t.ageSmoker.no;
                const isSelected = smokerChoice === id;
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSmokerChoice(id)}
                    className={choiceButtonClass(isSelected)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </SurveyBrownPanel>
        </SurveyBrownPanelInset>

        <div className="mt-12 flex flex-col items-center gap-3">
          {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
          <MetallicPillButton
            type="button"
            size="sm"
            className="metallic-pill--lang-home"
            onClick={handleSubmit}
          >
            {t.ageSmoker.submit}
          </MetallicPillButton>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
